import { Bell, HelpCircle, Inbox, LogOut, MenuIcon, Search } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

interface HeaderProps {
  onMenuClick: () => void
  sidebarOpen: boolean
  onLogout: () => void
}

const ROUTE_TITLES: Record<string, string> = {
  '/': 'Dashboard',
  '/events': 'Events',
  '/events/new': 'Create Event',
}

export default function Header({ onMenuClick, sidebarOpen, onLogout }: HeaderProps) {
  const { pathname } = useLocation()
  const title = ROUTE_TITLES[pathname] ?? 'Admin'
  const [profileOpen, setProfileOpen] = useState(false)
  const profileRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileOpen(false)
      }
    }
    if (profileOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [profileOpen])

  return (
    <header className="relative z-[10] flex w-full min-w-0 items-center gap-4 px-7 h-[60px] shrink-0 border-b border-border-dark/9 bg-header-bg/96">
      <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
        <button
          type="button"
          onClick={onMenuClick}
          aria-label={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
          aria-expanded={sidebarOpen}
          aria-controls="primary-sidebar"
          className="shrink-0 rounded-lg p-1.5 text-text-muted transition-colors hover:bg-border lg:hidden -ml-1"
        >
          <MenuIcon />
        </button>

        <nav
          aria-label="Breadcrumb"
          className="hidden min-w-0 shrink-0 items-center gap-1.5 text-xs-plus leading-4 tracking-wide whitespace-nowrap md:flex capitalize"
        >
          <span className="text-text-secondary font-medium">Admin</span>
          <span className="text-checkbox-border" aria-hidden="true">/</span>
          <span className="text-text-secondary font-medium">Content</span>
          <span className="text-checkbox-border" aria-hidden="true">/</span>
          <span className="font-semibold text-text-dark">{title}</span>
        </nav>

        <div className="min-w-0 max-w-[322px] flex-1 px-0 sm:pl-2 sm:pr-2">
          <label className="flex min-w-0 cursor-text items-center gap-2.5 rounded-md border border-border-dark/9 bg-warm-cream px-3 py-2 sm:px-3.5 focus-within:border-transparent">
            <Search aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-ink-muted" />
            <input
              type="text"
              aria-label="Global search"
              placeholder="Search members, events, episodes, products…"
              className="sidebar-quick-search-input min-w-0 w-full bg-transparent text-sm-alt text-text-dark placeholder:text-checkbox-border focus:outline-none focus-visible:outline-none focus:ring-0 focus:ring-offset-0"
            />
          </label>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-0.5 text-text-secondary sm:px-1 lg:pr-0">
        <button
          type="button"
          title="Help — coming soon"
          className="hidden md:flex p-2 rounded-md hover:bg-border transition-colors"
          aria-label="Help"
        >
          <HelpCircle className="w-4 h-4" />
        </button>
        <button
          type="button"
          title="Notifications — coming soon"
          className="hidden md:flex p-2 rounded-md hover:bg-border transition-colors"
          aria-label="Notifications"
        >
          <Bell className="w-4 h-4" />
        </button>
        <button
          type="button"
          title="Messages — coming soon"
          className="p-2 rounded-md hover:bg-border transition-colors"
          aria-label="Messages"
        >
          <Inbox className="w-4 h-4 " />
        </button>
        <span
          aria-hidden="true"
          className="mx-2 h-[22px] w-px shrink-0 self-center bg-border-dark/9"
        />
        <div ref={profileRef} className="relative">
          <button
            type="button"
            aria-label="User profile"
            aria-expanded={profileOpen}
            aria-haspopup="true"
            onClick={() => setProfileOpen((o) => !o)}
            className="w-8.5 h-8.5 rounded-full shrink-0 bg-linear-to-br from-orange-400 via-pink-400 to-purple-500 ring-2 ring-white hover:ring-ring-avatar transition-all flex items-center justify-center text-white text-xs-plus font-bold"
          >
            LH
          </button>

          {profileOpen && (
            <div className="absolute right-0 top-full mt-1.5 w-36 rounded-lg bg-white shadow-lg border border-divider py-1 z-100">
              <button
                type="button"
                onClick={() => {
                  setProfileOpen(false)
                  onLogout()
                }}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm-plus text-text-strong hover:bg-border transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
